/**
 * Access Control was fifteen sidebar entries deep, several of them two views of
 * the same question. They are grouped here by the question each answers — who
 * holds a role, what a role may do, what the platform enforces, what happened —
 * so the sidebar carries five entries and the rest become tabs.
 *
 * Kept free of React so the id resolution can be tested on its own: every legacy
 * /superadmin/access-control/<view> link has to keep landing on the right tab.
 */

export const ACCESS_GROUPS = [
  {
    label: 'Overview',
    tabs: [{ id: 'dashboard', label: 'Overview' }],
  },
  {
    label: 'Roles',
    tabs: [
      { id: 'directory', label: 'Roles' },
      { id: 'roles', label: 'Hierarchy' },
      { id: 'permission-groups', label: 'Permission groups' },
      { id: 'group-permissions', label: 'Tenant assignment' },
      { id: 'user-permissions', label: 'User overrides' },
    ],
  },
  {
    label: 'Permissions',
    tabs: [
      { id: 'platform-matrix', label: 'Platform matrix' },
      { id: 'matrix', label: 'Tenant matrix' },
      { id: 'role-permissions', label: 'By role' },
      { id: 'module-permissions', label: 'Modules' },
      { id: 'menu-permissions', label: 'Menus' },
      { id: 'permissions', label: 'Catalogue' },
    ],
  },
  {
    label: 'Policies',
    tabs: [
      { id: 'policies', label: 'Access policies' },
      { id: 'staff', label: 'Platform users' },
    ],
  },
  {
    label: 'Activity',
    tabs: [
      { id: 'audit', label: 'Audit log' },
      { id: 'simulator', label: 'Simulator' },
    ],
  },
];

export const ACCESS_PREFIX = 'access-control/';

// Each sidebar entry points at the first tab of its group.
export const ACCESS_NAV_ITEMS = ACCESS_GROUPS.map((group) => ({
  id: `${ACCESS_PREFIX}${group.tabs[0].id}`,
  label: group.label,
}));

export const accessGroupFor = (view) =>
  ACCESS_GROUPS.find((group) => group.tabs.some((tab) => tab.id === view)) || ACCESS_GROUPS[0];

/** The tab actually shown for a view — unknown ids fall back to the group's first. */
export const accessViewFor = (view) => {
  const group = accessGroupFor(view);
  return group.tabs.some((tab) => tab.id === view) ? view : group.tabs[0].id;
};

/** Which sidebar entry to light up for any access-control view, old id or new. */
export const accessNavIdFor = (view) => `${ACCESS_PREFIX}${accessGroupFor(view).tabs[0].id}`;

/**
 * Views that read or write one tenant's access. Every one of these ran against a
 * hardcoded tenant 1 regardless of what the operator was looking at, so editing
 * "tenant 12's permissions" silently edited tenant 1's. The workspace shows a
 * tenant picker for exactly this set and passes the choice down.
 */
export const TENANT_SCOPED_VIEWS = [
  'roles', 'matrix', 'role-permissions', 'module-permissions', 'menu-permissions',
  'group-permissions', 'simulator',
];

export const isTenantScopedView = (view) => TENANT_SCOPED_VIEWS.includes(accessViewFor(view));
